import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  FormControl,
  InputLabel,
  useMediaQuery,
  Select,
  MenuItem,
  IconButton
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import axios from "../../../api/axios";
import styles from './EventCheckin.module.css';

const EventCheckin = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();

  const [displayData, setDisplayData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // For the input field
  const [searchTerm, setSearchTerm] = useState(''); // For actual search execution
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const formatApiError = (e) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (data?.error) return data.error;
    if (typeof data === 'string') return `${status ? `${status}: ` : ''}${data}`.trim();
    if (data) return `${status ? `${status}: ` : ''}${JSON.stringify(data)}`.trim();
    return e?.message || 'Unknown error';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      
      return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const handleManageCheckins = useCallback((eventId) => {
    navigate(`/admin/checkin/${eventId}`);
  }, [navigate]);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    setIsLoading(true);
    setError(false);
    try {
      // Build query parameters for server-side pagination, search, and filtering.
      // Uses the admin /withCounts endpoint (includes private events, has
      // server-side search) so newly created events reliably show up and can be
      // found regardless of which page they land on.
      const page = paginationModel.page + 1; // API uses 1-based page indexing
      const limit = paginationModel.pageSize;
      const upcomingParam = showUpcomingOnly ? '&upcoming=true' : '';
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';

      const resp = await axios.get(`/api/events/withCounts?page=${page}&limit=${limit}${upcomingParam}${searchParam}`);
      const events = resp?.data?.events || [];
      const pagination = resp?.data?.pagination || {};

      setDisplayData(events);
      setTotalCount(pagination.totalCount || 0);
      
      if (!silent) enqueueSnackbar('Events loaded', { variant: 'success' });
    } catch (e) {
      setError(true);
      if (!silent) enqueueSnackbar(formatApiError(e) || 'Failed to load events', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [axios, enqueueSnackbar, paginationModel.page, paginationModel.pageSize, searchTerm, showUpcomingOnly]);

  // Fetch data when pagination, search, or filter changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to first page when search term or filter changes
  useEffect(() => {
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, showUpcomingOnly]);

  const handleSearchSubmit = useCallback(() => {
    // Update searchTerm which will trigger the useEffect to fetch data
    setSearchTerm(searchInput);
  }, [searchInput]);

  const handleSearchKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);

  const columns = useMemo(() => [
    {
      field: 'BP_Title',
      headerName: 'Event Title',
      minWidth: 250,
      flex: 1.5
    },
    {
      field: 'BP_StartDate',
      headerName: 'Start Date/Time',
      minWidth: 180,
      flex: 1,
      valueFormatter: (params) => formatDateTime(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleManageCheckins(params.row.EventsID)}
          className={styles.manageButton}
        >
          Manage Check-Ins
        </Button>
      )
    }
  ], [handleManageCheckins]);

  // Loading state
  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Event Check-In</h1>
                <p>Check attendees in as they arrive and track real-time attendance</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <Box className={styles.loadingContainer}>
              <CircularProgress />
              <Typography className={styles.loadingText}>Loading events...</Typography>
            </Box>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Event Check-In</h1>
                <p>Check attendees in as they arrive and track real-time attendance</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <Box className={styles.errorContainer}>
              <Typography color="error">Error occurred while fetching events. Please contact an admin.</Typography>
            </Box>
          </div>
        </div>
      </main>
    );
  }

  // Calculate pagination info
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = Math.min(startIndex + paginationModel.pageSize, totalCount);

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Event Check-In</h1>
              <p>Check attendees in as they arrive and track real-time attendance</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        <div className="">
          <Box className={styles.container}>
            {/* Search and Filter Section */}
            <Paper className={styles.searchPaper}>
              <Box className={styles.searchContainer}>
                <Box className={styles.filterContainer}>
                  <FormControl className={styles.filterSelect}>
                    <InputLabel>Show Events</InputLabel>
                    <Select
                      value={showUpcomingOnly ? 'upcoming' : 'all'}
                      onChange={(e) => setShowUpcomingOnly(e.target.value === 'upcoming')}
                      label="Show Events"
                    >
                      <MenuItem value="upcoming">Upcoming Events Only</MenuItem>
                      <MenuItem value="all">All Events</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  className={styles.searchField}
                  label="Search Events"
                  placeholder="Search by title or location"
                  value={searchInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchInput(val);
                    // When the field is cleared (backspaced to empty), reload the
                    // full list by clearing the executed search term.
                    if (val === '' && searchTerm !== '') {
                      setSearchTerm('');
                    }
                  }}
                  onKeyPress={handleSearchKeyPress}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleSearchSubmit}
                          edge="end"
                          aria-label="search"
                          color="primary"
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            </Paper>

            {/* Data Table */}
            <Paper className={styles.dataGridPaper}>
              <DataGrid
                className="eventCheckinDataGrid"
                rows={displayData}
                columns={columns}
                getRowId={(row) => row.EventsID}
                disableRowSelectionOnClick
                autoHeight
                hideFooterPagination
                rowHeight={75}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No events found</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search term.</Typography>
                    </Box>
                  ),
                }}
              />
            </Paper>

            {/* Custom Pagination */}
            <Paper className={styles.paginationPaper}>
              <Stack
                className={styles.paginationStack}
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ width: '100%' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount > 0 ? startIndex + 1 : 0} to{' '}
                  {endIndex} of {totalCount} Events
                </Typography>

                <Stack className={styles.paginationControls} direction="row" alignItems="center">
                  <Pagination
                    count={pageCount}
                    page={paginationModel.page + 1}
                    onChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    className="eventCheckinPagination"
                  />
                </Stack>

                <Box className={styles.pageSizeSelector}>
                  <Typography variant="body2" color="text.secondary">
                    Show:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={paginationModel.pageSize}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
                    }}
                    SelectProps={{ native: true }}
                    className={`${styles.pageSizeField} pageSizeField`}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </TextField>
                  <Typography variant="body2" color="text.secondary">
                    per page
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </div>
      </div>
    </main>
  );
};

export default EventCheckin;
